import { useCallback, useEffect } from 'react';
import { Button, Card, Segmented, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useFlowbackStore } from '@/store/flowbackStore';
import { type FlowbackRecord, type FlowbackStatus } from '@/api/requirementFlowback';

const STATUS_FILTERS: { label: string; value: FlowbackStatus }[] = [
  { label: '全部', value: 'all' },
  { label: '待评审', value: 'pending' },
  { label: '已合入', value: 'approved' },
  { label: '已忽略', value: 'rejected' },
];

const STATUS_TAG: Record<FlowbackRecord['status'], { color: string; text: string }> = {
  pending: { color: 'gold', text: '待评审' },
  approved: { color: 'green', text: '已合入' },
  rejected: { color: 'default', text: '已忽略' },
};

const RequirementFlowback: React.FC = () => {
  const { records, total, isLoading, listParams, fetchList } = useFlowbackStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(() => {
    fetchList();
  }, [fetchList]);

  // 点击评审/查看：跳转到"工具→需求同步"，预填该回流的源/目标/类别并带 flowbackId
  const goToSync = (record: FlowbackRecord) => {
    const sp = new URLSearchParams({
      srcProject: record.project_name,
      srcSubcatagory: record.subcategory,
      desProject: 'mainLine',
      desSubcatagory: record.subcategory,
      flowbackId: String(record.id),
      requirementId: record.requirement_id,
      mainlineReqId: record.mainline_req_id || '',
    });
    navigate(`/requirement-sync?${sp.toString()}`);
  };

  // 点击需求ID：跳转到该需求所属项目空间的需求显示界面（自动选中该需求）
  const goToProjectReq = (record: FlowbackRecord) => {
    navigate(`/requirements/${record.project_id}?reqId=${encodeURIComponent(record.requirement_id)}`);
  };

  const columns: ColumnsType<FlowbackRecord> = [
    {
      title: '项目',
      dataIndex: 'project_name',
      key: 'project_name',
      ellipsis: true,
    },
    {
      title: '需求ID',
      dataIndex: 'requirement_id',
      key: 'requirement_id',
      render: (text: string, record) => (
        <a onClick={() => goToProjectReq(record)}>{text}</a>
      ),
    },
    { title: '需求类别', dataIndex: 'subcategory', key: 'subcategory' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: FlowbackRecord['status']) => (
        <Tag color={STATUS_TAG[s].color}>{STATUS_TAG[s].text}</Tag>
      ),
    },
    { title: '发起人', dataIndex: 'initiator', key: 'initiator' },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string) => (v ? new Date(v).toLocaleString() : ''),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record) =>
        record.status === 'pending' ? (
          <Button type="link" size="small" onClick={() => goToSync(record)}>
            评审
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title="需求回流管理"
        extra={
          <>
            <Segmented
              value={listParams.status ?? 'all'}
              onChange={(v) => fetchList({ status: v as FlowbackStatus, page: 1 })}
              options={STATUS_FILTERS}
            />
            <Button size="small" onClick={reload} style={{ marginLeft: 8 }}>
              刷新
            </Button>
          </>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={records}
          loading={isLoading}
          pagination={{
            current: listParams.page ?? 1,
            pageSize: listParams.page_size ?? 20,
            total,
            showSizeChanger: true,
            onChange: (page, page_size) => fetchList({ page, page_size }),
          }}
        />
      </Card>
    </div>
  );
};

export default RequirementFlowback;
